import {IVideoResolution} from "../../video/models/video-player.view-model.interface";

export type SlideType = 'LiveStream' | 'VideoStream' | 'Promotion';

export enum VideoSlidesAnalyticsEventEnum {

    /**
     * Reported when the slide becomes active (becomes the current slide)
     */
    SlideActivated = 'SlideActivated',

    /**
     * Reported when the slide becomes inactive active.
     */
    SlideDeactivated = 'SlideDeactivated',

    /**
     * Send when the video started playing
     */
    PlayVideoStarted = "PlayVideoStarted",

    /**
     * Reported when the user click on the play game button from a video slide.
     */
    PlayGame = "PlayGame",

    /**
     * Reported when the user bookmark a game using the bookmark button.
     */
    BookGame = "BookGame",

    /**
     * Reported when a bookmark for a game is removed
     */
    RemoveBookedGame = "RemoveBookedGame",

    /**
     * Reported when the user mutes the video sound
     */
    Mute = "Mute",

    /**
     * Reported when the user unmutes the video sound
     */
    Unmute = "Unmute",

    /**
     * Reported when the user click on the retry play button and this attempt was successful.
     */
    RetryPlayVideoSuccess = "RetryPlayVideoSuccess",

    /**
     * Reported when an attempt to retry a failed video also fails.
     */
    RetryPlayVideoError = 'RetryPlayVideoError',

    /**
     * Reported when the video is paused due to the application browser tab becomes inactive.
     */
    PauseVideoOnAppSleep = "PauseVideoOnAppSleep",

    /**
     * Triggered when resuming a video play was successful. Resume play happens for example when the user comes back on a previously inactive browser tab.
     */
    ResumePlayVideoSuccess = "ResumePlayVideoSuccess",

    /**
     * Reported when there is an error while resuming a video play that was paused previously because of the browser tab becoming inactive.
     */
    ResumePlayVideoError = 'ResumePlayVideoError',

    /**
     * Reported when there was an error playing a video
     */
    PlayVideoError = 'PlayVideoError',

    /**
     * Reported when there was an error in the preload stage of the video.
     */
    PreloadVideoError = 'PreloadVideoError',

    /**
     * Reported when there was an error loading the video poster
     */
    VideoPosterLoadError = 'VideoPosterLoadError',

    /**
     * Reported when there are more than 15 dropped video frames.
     */
    DroppedVideoFrames = 'DroppedVideoFrames',

    /**
     * Reported when the watch time reach 2 minutes for a live stream video.
     */
    VideoWatchTimeLimitReached = 'VideoWatchTimeLimitReached',

    /**
     * Reported when the video resolution changed.
     */
    VideoResolutionChanged = 'VideoResolutionChanged',

    /**
     * Reported when the video player does buffering but only if the buffering time is more than 1 second.
     */
    Buffering = "Buffering",


}

export interface IVideoSlidesAnalyticsData {
    eventName: VideoSlidesAnalyticsEventEnum;

    /**
     * Can be 'LiveStream' | 'VideoStream' | 'Promotion'
     */
    slideType: SlideType;

    /**
     * The ID of the game that is shown in the current slide
     */
    gameType: number;

    /**
     * The name of the game that is shown in the current slide
     */
    gameName: string;

    /**
     * The video total duration time.
     */
    videoDuration?: number;

    /**
     * How long the user watched this video on this session. If for example the user watches 10 seconds and swipe to the next video then 10 seconds will be reported.
     * But if then swipe back and watch another 5 seconds and swipe again to the next slide then 15 seconds will be reported.
     */
    videoWatchTime: number;

    /**
     * How much time the user spend on the current slide from the moment the slide was activated until the slide was deactivated.
     * Reported only with the SlideDeactivated event.
     */
    slideActiveTime?: number;

    /**
     * It is true when the videoWatchTime reach 2 minutes for a live stream video.
     */
    watchTimeLimitReached: boolean;

    /**
     * The error name for the events that reports errors
     */
    errorName: string;

    /**
     * The error message for the events that reports errors
     */
    errorMessage: string;

    /**
     * True if video sound is muted
     */
    isMuted: boolean;

    /**
     * True if video is in the playing mode
     */
    isPlaying: boolean;

    /**
     * The number of dropped frames. It is reported only if droppedFrames > 15
     */
    droppedFrames?: number;

    /**
     * The video resolution before the resolution change occurred. It is reported only with the VideoResolutionChanged event
     */
    previousResolution?: IVideoResolution;

    /**
     * Current video resolution.
     */
    currentResolution: IVideoResolution;

    /**
     * Current filter ID selected by the user in the video-slides page
     */
    currentFilter: string;

    /**
     * The total number of slides for the current applied filter
     */
    slidesCount: number;

    /**
     * The index position of the slide. This value can be between 0 and slidesCount - 1
     */
    realSlideIndex: number;

    /**
     * The video slides swiper is a circular (infinite) swiper. This means that the swiping down never ends.
     * This value can be higher than slidesCount - 1 and when it is higher it means that the user swiped at least once through all the available slides.
     */
    virtualSlideIndex: number;

    /**
     * The direction of the swipe.
     * -1 => Go to previous
     * 1  => Go to next slide
     */
    swipeDirection: number;

    /**
     * Reported when the video player does buffering.
     * It is reported along with the Buffering event only if the buffering time is higher than 1 second.
     */
    bufferingTime: number;
}